import { toRaw } from 'vue'
import { useLocalStorage } from '@vueuse/core'

import type { Settings } from '@brighterly/lib-core-types/customer/interfaces'
import type { SettingsStore, EnsureOptions } from './interfaces'

import { useSettingsApi } from '@api/me/settings'

import { parseStoredJson } from '@helpers/safe-local-storage'

const STORAGE_KEY = 'settings'

const persist = (state: SettingsStore): void => {
  const ls = useLocalStorage<Settings | null>(STORAGE_KEY, null, {
    serializer: {
      read: (v) => parseStoredJson<Settings>(v),
      write: (v) => (v ? JSON.stringify(v) : ''),
    },
  })

  const settings = toRaw(state.settings) as Settings | null

  ls.value = settings ? { ...settings } : null
}

const setSettings = (state: SettingsStore, settings: Settings): void => {
  state.settings = settings
  persist(state)
}

const clear = (state: SettingsStore): void => {
  state.settings = null
  persist(state)
}

export const actions = {
  hydrateFromStorage (this: SettingsStore): void {
    if (this._hydrated) return

    const ls = useLocalStorage<Settings | null>(STORAGE_KEY, null, {
      serializer: {
        read: (v) => parseStoredJson<Settings>(v),
        write: (v) => (v ? JSON.stringify(v) : ''),
      },
    })

    this.settings = ls.value ? { ...ls.value } : null
    this._hydrated = true
  },
  async ensure (this: SettingsStore, options: EnsureOptions = {}): Promise<SettingsStore['settings']> {
    if (!options.force && this.settings) {
      this._initialLoaded = true
      return this.settings
    }

    if (this.settings && options.background) {
      void useSettingsApi().get().finally(() => {
        this._initialLoaded = true
      })

      return this.settings
    }

    try {
      return await useSettingsApi().get()
    } finally {
      this._initialLoaded = true
    }
  },
  setSettings (this: SettingsStore, settings: Settings): void {
    return setSettings(this, settings)
  },
  clear (this: SettingsStore): void {
    return clear(this)
  },
}
